import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Button,
  Chip,
  Divider,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Psychology as PsychologyIcon,
  Upload as UploadIcon,
  Chat as ChatIcon,
  Description as DescriptionIcon,
  History as HistoryIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckCircleIcon,
  InfoOutlined as InfoIcon,
  LightbulbOutlined as LightbulbIcon,
  WarningAmber as WarningIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';

const RagHilfe: React.FC = () => {
  const [expanded, setExpanded] = useState<string | false>('schnellstart');

  const handleChange = (panel: string) => (_: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          <HelpIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Wissensdatenbank - Hilfe & Anleitung
        </Typography>
        <Typography variant="body1" color="textSecondary">
          Erfahren Sie, wie Sie die Wissensdatenbank optimal nutzen können
        </Typography>
      </Box>

      {/* Was ist RAG? */}
      <Paper sx={{ p: 3, mb: 3, bgcolor: 'primary.50' }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <PsychologyIcon sx={{ mr: 1 }} color="primary" />
          Was ist die Wissensdatenbank (RAG)?
        </Typography>
        <Typography variant="body1" paragraph>
          RAG steht für <strong>Retrieval-Augmented Generation</strong> (dt.: Abrufunterstützte Generierung).
          Die Wissensdatenbank ermöglicht es Ihnen, Ihre eigenen Dokumente hochzuladen und dann intelligente
          Fragen dazu zu stellen. Das System durchsucht Ihre Dokumente und liefert präzise Antworten mit
          Quellenangaben.
        </Typography>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <UploadIcon fontSize="large" color="primary" />
              <Typography variant="body2">1. Dokumente hochladen</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <PsychologyIcon fontSize="large" color="primary" />
              <Typography variant="body2">2. KI analysiert</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <ChatIcon fontSize="large" color="primary" />
              <Typography variant="body2">3. Fragen stellen</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircleIcon fontSize="large" color="primary" />
              <Typography variant="body2">4. Antworten erhalten</Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Funktionen Übersicht */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <UploadIcon color="success" sx={{ fontSize: 40 }} />
              <Typography variant="h6" sx={{ mt: 1 }}>
                Dokumente hochladen
              </Typography>
              <Typography variant="body2" color="textSecondary">
                PDF, Word, Text, Markdown
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <ChatIcon color="info" sx={{ fontSize: 40 }} />
              <Typography variant="h6" sx={{ mt: 1 }}>
                Intelligente Suche
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Finden Sie Informationen schnell
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <DescriptionIcon color="warning" sx={{ fontSize: 40 }} />
              <Typography variant="h6" sx={{ mt: 1 }}>
                Quellen-Nachweis
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Sehen Sie, woher die Info kommt
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <HistoryIcon color="error" sx={{ fontSize: 40 }} />
              <Typography variant="h6" sx={{ mt: 1 }}>
                Historie
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Alle Anfragen protokolliert
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Accordion Sections */}

      {/* Schnellstart */}
      <Accordion expanded={expanded === 'schnellstart'} onChange={handleChange('schnellstart')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            <CheckCircleIcon sx={{ mr: 1, verticalAlign: 'middle' }} color="success" />
            Schnellstart - In 3 Schritten loslegen
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <List>
            <ListItem>
              <ListItemIcon>
                <Chip label="1" color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Dokument hochladen"
                secondary={
                  <>
                    Navigieren Sie zu <strong>Menü → Wissensdatenbank</strong> und klicken Sie auf
                    <strong> "Dokument hochladen"</strong>. Wählen Sie eine Datei (PDF, DOCX, TXT, MD)
                    und warten Sie, bis der Status "verarbeitet" angezeigt wird.
                  </>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Chip label="2" color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Chat öffnen"
                secondary={
                  <>
                    Klicken Sie auf das <strong>Gehirn-Symbol</strong> rechts unten auf jeder Seite
                    oder nutzen Sie den Chat-Tab auf der Wissensdatenbank-Seite.
                  </>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Chip label="3" color="primary" />
              </ListItemIcon>
              <ListItemText
                primary="Frage stellen"
                secondary={
                  <>
                    Geben Sie Ihre Frage ein und drücken Sie <strong>Enter</strong> oder klicken
                    Sie auf <strong>Senden</strong>. Die Antwort erscheint mit Quellen-Angaben.
                  </>
                }
              />
            </ListItem>
          </List>
        </AccordionDetails>
      </Accordion>

      {/* Dokumente hochladen */}
      <Accordion expanded={expanded === 'upload'} onChange={handleChange('upload')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            <UploadIcon sx={{ mr: 1, verticalAlign: 'middle' }} color="success" />
            Dokumente hochladen & verwalten
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="subtitle1" gutterBottom>
            Unterstützte Dateiformate:
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Chip label="PDF (.pdf)" sx={{ m: 0.5 }} />
            <Chip label="Word (.docx)" sx={{ m: 0.5 }} />
            <Chip label="Text (.txt)" sx={{ m: 0.5 }} />
            <Chip label="Markdown (.md)" sx={{ m: 0.5 }} />
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Tipp:</strong> Je besser strukturiert Ihre Dokumente sind (mit klaren Überschriften,
            Absätzen und Formatierungen), desto bessere Antworten erhalten Sie.
          </Alert>

          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
            So laden Sie ein Dokument hoch:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
              <ListItemText primary='Klicken Sie auf "Dokument hochladen"' />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
              <ListItemText primary="Wählen Sie eine unterstützte Datei aus" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
              <ListItemText primary="Warten Sie, bis Status 'verarbeitet' erscheint" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
              <ListItemText primary="Dokument ist nun durchsuchbar!" />
            </ListItem>
          </List>

          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
            Dokument löschen:
          </Typography>
          <Typography variant="body2" paragraph>
            Klicken Sie auf das <strong>Papierkorb-Symbol</strong> neben dem Dokument im Tab "Dokumente".
            Gelöschte Dokumente können nicht mehr durchsucht werden, aber alte Chat-Antworten bleiben
            in der Historie erhalten.
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* Chat nutzen */}
      <Accordion expanded={expanded === 'chat'} onChange={handleChange('chat')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            <ChatIcon sx={{ mr: 1, verticalAlign: 'middle' }} color="info" />
            Chat nutzen - Richtig Fragen stellen
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="subtitle1" gutterBottom>
            Es gibt zwei Möglichkeiten, den Chat zu nutzen:
          </Typography>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    <PsychologyIcon fontSize="small" /> Floating Chat-Widget
                  </Typography>
                  <Typography variant="body2">
                    Klicken Sie auf das Gehirn-Symbol rechts unten auf jeder Seite.
                    Kompakte Ansicht für schnelle Fragen.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" gutterBottom>
                    <DescriptionIcon fontSize="small" /> Wissensdatenbank-Seite
                  </Typography>
                  <Typography variant="body2">
                    Tab "Chat" auf der Hauptseite. Größere Ansicht mit detaillierten Quellen
                    und Antwortzeiten.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Alert severity="success" icon={<LightbulbIcon />} sx={{ mb: 2 }}>
            <Typography variant="subtitle2">Tipps für gute Fragen:</Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="✅ Seien Sie spezifisch: 'Was sind die Anforderungen für Feature X?'" />
              </ListItem>
              <ListItem>
                <ListItemText primary="✅ Verwenden Sie Begriffe aus Ihren Dokumenten" />
              </ListItem>
              <ListItem>
                <ListItemText primary="✅ Stellen Sie eine Frage pro Anfrage" />
              </ListItem>
              <ListItem>
                <ListItemText primary="❌ Vermeiden Sie zu allgemeine Fragen wie 'Was steht in den Dokumenten?'" />
              </ListItem>
            </List>
          </Alert>

          <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
            Beispiel-Fragen:
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Typography variant="body2" component="div">
              <code>Was sind die Hauptthemen in den Dokumenten?</code>
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" component="div">
              <code>Fasse die Informationen über [Thema] zusammen.</code>
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" component="div">
              <code>Gibt es Informationen zu [spezifische Frage]?</code>
            </Typography>
            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" component="div">
              <code>Erkläre mir [Begriff] aus den Dokumenten.</code>
            </Typography>
          </Paper>

          <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
            Quellen prüfen:
          </Typography>
          <Typography variant="body2" paragraph>
            Jede Antwort zeigt die <strong>Quell-Dokumente</strong> an, aus denen die Information stammt.
            Wenn keine Quellen angezeigt werden, basiert die Antwort nicht auf Ihren Dokumenten.
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* Statistiken */}
      <Accordion expanded={expanded === 'stats'} onChange={handleChange('stats')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            <TrendingUpIcon sx={{ mr: 1, verticalAlign: 'middle' }} color="warning" />
            Statistiken & Historie
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="subtitle1" gutterBottom>
            Dashboard-Statistiken:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon><DescriptionIcon color="primary" /></ListItemIcon>
              <ListItemText
                primary="Dokumente"
                secondary="Anzahl hochgeladener Dokumente + verwendeter Speicherplatz"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><ChatIcon color="primary" /></ListItemIcon>
              <ListItemText
                primary="Chats gesamt"
                secondary="Alle Chat-Anfragen seit Beginn"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><TrendingUpIcon color="primary" /></ListItemIcon>
              <ListItemText
                primary="Letzte 7 Tage"
                secondary="Chat-Aktivität der letzten Woche"
              />
            </ListItem>
            <ListItem>
              <ListItemIcon><PsychologyIcon color="primary" /></ListItemIcon>
              <ListItemText
                primary="Ø Antwortzeit"
                secondary="Durchschnittliche Zeit bis zur Antwort in Millisekunden"
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle1" gutterBottom>
            Chat-Historie:
          </Typography>
          <Typography variant="body2" paragraph>
            Im Tab <strong>"Historie"</strong> finden Sie alle Ihre bisherigen Chat-Anfragen
            mit den dazugehörigen Antworten. Dies ist nützlich zum:
          </Typography>
          <List dense>
            <ListItem>
              <ListItemIcon><CheckCircleIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Nachschlagen früherer Antworten" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Analysieren häufig gestellter Fragen" />
            </ListItem>
            <ListItem>
              <ListItemIcon><CheckCircleIcon fontSize="small" /></ListItemIcon>
              <ListItemText primary="Qualitätskontrolle der Antworten" />
            </ListItem>
          </List>
        </AccordionDetails>
      </Accordion>

      {/* FAQ */}
      <Accordion expanded={expanded === 'faq'} onChange={handleChange('faq')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            <InfoIcon sx={{ mr: 1, verticalAlign: 'middle' }} color="error" />
            Häufige Fragen (FAQ)
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              ❓ Warum bekomme ich keine Antwort auf meine Frage?
            </Typography>
            <Typography variant="body2" paragraph>
              Prüfen Sie, ob relevante Dokumente hochgeladen sind. Die Information könnte
              nicht in den Dokumenten enthalten sein. Versuchen Sie, die Frage anders zu formulieren.
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              ❓ Wie genau sind die Antworten?
            </Typography>
            <Typography variant="body2" paragraph>
              Die Antworten basieren auf Ihren hochgeladenen Dokumenten. Die KI interpretiert
              und fasst zusammen. Prüfen Sie immer die Quellen-Angaben, um die Genauigkeit
              zu verifizieren.
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              ❓ Können andere Mandanten meine Dokumente sehen?
            </Typography>
            <Typography variant="body2" paragraph>
              <strong>Nein!</strong> Jeder Mandant hat einen separaten Workspace.
              Dokumente und Chats sind vollständig isoliert. Mandanten-übergreifender
              Zugriff ist nicht möglich.
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              ❓ Wie viele Dokumente kann ich hochladen?
            </Typography>
            <Typography variant="body2" paragraph>
              Es gibt keine feste Begrenzung der Anzahl. Beachten Sie jedoch den verfügbaren
              Speicherplatz. Sehr große Mengen können die Antwortzeit beeinflussen.
            </Typography>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              ❓ Was passiert, wenn ich ein Dokument lösche?
            </Typography>
            <Typography variant="body2" paragraph>
              Das Dokument wird aus der Wissensdatenbank entfernt und der Chat kann nicht
              mehr darauf zugreifen. Alte Chat-Antworten bleiben jedoch in der Historie erhalten.
            </Typography>
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Best Practices */}
      <Accordion expanded={expanded === 'best-practices'} onChange={handleChange('best-practices')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            <LightbulbIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Best Practices - Tipps für beste Ergebnisse
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Alert severity="success" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Dokumente vorbereiten:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="✅ Verwenden Sie klare Überschriften" />
              </ListItem>
              <ListItem>
                <ListItemText primary="✅ Strukturieren Sie Inhalte logisch" />
              </ListItem>
              <ListItem>
                <ListItemText primary="✅ Vermeiden Sie gescannte PDFs (OCR-Qualität)" />
              </ListItem>
              <ListItem>
                <ListItemText primary="✅ Nutzen Sie aussagekräftige Dateinamen" />
              </ListItem>
            </List>
          </Alert>

          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Fragen stellen:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="✅ Seien Sie spezifisch" />
              </ListItem>
              <ListItem>
                <ListItemText primary="✅ Stellen Sie eine Frage pro Anfrage" />
              </ListItem>
              <ListItem>
                <ListItemText primary="✅ Verwenden Sie Begriffe aus den Dokumenten" />
              </ListItem>
              <ListItem>
                <ListItemText primary='✅ Nutzen Sie Kontext ("In Dokument X steht...")' />
              </ListItem>
            </List>
          </Alert>

          <Alert severity="warning">
            <Typography variant="subtitle2" gutterBottom>
              Antworten bewerten:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="✅ Prüfen Sie die angegebenen Quellen" />
              </ListItem>
              <ListItem>
                <ListItemText primary="✅ Vergleichen Sie mit Originaltext" />
              </ListItem>
              <ListItem>
                <ListItemText primary="✅ Nutzen Sie Antwortzeit als Qualitätsindikator" />
              </ListItem>
              <ListItem>
                <ListItemText primary="✅ Bei Unsicherheit: Frage umformulieren" />
              </ListItem>
            </List>
          </Alert>
        </AccordionDetails>
      </Accordion>

      {/* Tastenkombinationen */}
      <Accordion expanded={expanded === 'keyboard'} onChange={handleChange('keyboard')}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">
            <InfoIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Tastenkombinationen
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <List>
            <ListItem>
              <ListItemText
                primary="Nachricht senden"
                secondary={<Chip label="Enter" size="small" />}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Neue Zeile im Chat"
                secondary={<Chip label="Shift + Enter" size="small" />}
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="Chat öffnen"
                secondary="Widget rechts unten klicken"
              />
            </ListItem>
          </List>
        </AccordionDetails>
      </Accordion>

      {/* Support */}
      <Paper sx={{ p: 3, mt: 3, bgcolor: 'info.50' }}>
        <Typography variant="h6" gutterBottom>
          <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} color="warning" />
          Benötigen Sie weitere Hilfe?
        </Typography>
        <Typography variant="body2" paragraph>
          Bei Problemen oder Fragen:
        </Typography>
        <List dense>
          <ListItem>
            <ListItemIcon><CheckCircleIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Prüfen Sie die Backend-Logs" />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckCircleIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Kontaktieren Sie Ihren Administrator" />
          </ListItem>
          <ListItem>
            <ListItemIcon><CheckCircleIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Prüfen Sie die technische Dokumentation (RAG_INTEGRATION_README.md)" />
          </ListItem>
        </List>
        <Button variant="contained" color="primary" href="/wissensdatenbank" sx={{ mt: 2 }}>
          Zur Wissensdatenbank
        </Button>
      </Paper>
    </Box>
  );
};

export default RagHilfe;
